/**
 * LOS DATOS QUE PIDE CADA MÉTODO DE PAGO.
 *
 * Un Pago Móvil necesita teléfono, cédula, banco y referencia; una tarjeta, los
 * últimos 4 dígitos y la aprobación; el efectivo, nada. Este componente dibuja
 * los campos que ese método concreto tenga configurados.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  PREGUNTAR NO ES EXIGIR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Los campos marcados con `*` bloquean el cobro; los demás se preguntan y se
 * guardan si vienen, pero no frenan la venta.
 *
 * La razón es del mostrador: el cliente enseña la captura del Pago Móvil en su
 * celular, y ahí muchas veces se ve la referencia y el teléfono pero no el
 * banco ni la cédula. Si los cuatro fueran obligatorios, **el cajero no podría
 * cobrar** — tendría que inventarse un dato o perder la venta, con cola detrás.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  EL TECLADO IMPORTA MÁS DE LO QUE PARECE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Cada tipo de campo abre el teclado correcto en el celular: numérico para el
 * teléfono y la cédula, correo para el correo. Pedirle un teléfono a un cajero
 * con el teclado de letras le cuesta tres segundos por venta — y esos tres
 * segundos son lo que hace que a la quinta venta deje de anotar la referencia.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Campo, cx } from "@/components/ui";
import { metodosPago } from "@/lib/endpoints";
import type { PaymentMethod } from "@/lib/types";

/** Un campo del catálogo, tal como lo describe el servidor. */
export type CampoDePago = {
  clave: string;
  etiqueta: string;
  ayuda: string;
  tipo: "texto" | "numero" | "telefono" | "documento" | "correo" | "banco" | "fecha";
  max_largo: number;
  en_recibo: boolean;
};

/** El catálogo se pide una vez y se guarda: no cambia nunca durante una sesión. */
export function useCatalogoDeCampos() {
  return useQuery({
    queryKey: ["catalogo-campos-pago"],
    queryFn: metodosPago.catalogoDeCampos,
    staleTime: Infinity,
  });
}

/** Qué teclado abrir en el celular según el tipo de dato. */
function tecladoDe(tipo: CampoDePago["tipo"]): {
  inputMode: "text" | "numeric" | "tel" | "email";
  type: string;
} {
  switch (tipo) {
    case "telefono":
      return { inputMode: "tel", type: "tel" };
    case "numero":
      // `numeric` y no `type="number"`: los campos numéricos del navegador traen
      // flechitas de subir y bajar que en una pantalla táctil solo estorban, y
      // además borran el valor si se escribe algo raro.
      return { inputMode: "numeric", type: "text" };
    case "documento":
      // La cédula es "numérica" para el teclado pero se escribe como texto:
      // mucha gente pone "V-12345678" y hay que dejarla.
      return { inputMode: "numeric", type: "text" };
    case "correo":
      return { inputMode: "email", type: "email" };
    default:
      return { inputMode: "text", type: "text" };
  }
}

export function CamposDelMetodo({
  metodo,
  valores,
  onCambiar,
  mostrarErrores,
  cliente,
}: {
  metodo: PaymentMethod | undefined;
  valores: Record<string, string>;
  onCambiar: (datos: Record<string, string>) => void;
  /** Solo se marcan en rojo cuando ya hay un monto escrito: antes sería regañar por nada. */
  mostrarErrores: boolean;
  /**
   * El cliente de la venta, si ya se eligió.
   *
   * ⚠️ Sirve para no volver a pedir lo que Softfi ya sabe. Ver
   * `useRellenarDelCliente`.
   */
  cliente?: { phone?: string | null; tax_id?: string | null } | null;
}) {
  const { data: catalogo } = useCatalogoDeCampos();

  const pedidos = metodo?.campos ?? [];
  useRellenarDelCliente({ pedidos, valores, onCambiar, cliente });

  if (!metodo || pedidos.length === 0) return null;

  const obligatorios = new Set(metodo.campos_obligatorios ?? []);
  const definiciones = new Map((catalogo ?? []).map((c) => [c.clave, c]));

  /*
    ⚠️ DOS COLUMNAS CUANDO CABEN, Y LOS OBLIGATORIOS PRIMERO.

    Cashea pide seis datos, y en una sola columna eran seis renglones que
    empujaban la inicial, el total y el botón de confirmar una pantalla hacia
    abajo. Se mide el hueco (`@container`), no la ventana: el cobro va en una
    columna que cambia de ancho. Y lo que impide cobrar va arriba, que es lo
    que hay que rellenar sí o sí.
  */
  const enOrden = [
    ...pedidos.filter((c) => obligatorios.has(c)),
    ...pedidos.filter((c) => !obligatorios.has(c)),
  ];

  return (
    <div className="@container mt-2">
    <div className="grid gap-2 @[26rem]:grid-cols-2">
      {enOrden.map((clave) => {
        const def = definiciones.get(clave);
        if (!def) return null;

        const esObligatorio = obligatorios.has(clave);
        const valor = valores[clave] ?? "";
        const falta = esObligatorio && !valor.trim();
        const teclado = tecladoDe(def.tipo);

        return (
          <Campo
            key={clave}
            type={teclado.type}
            inputMode={teclado.inputMode}
            value={valor}
            onChange={(e) => onCambiar({ ...valores, [clave]: e.target.value })}
            placeholder={esObligatorio ? `${def.etiqueta} (obligatorio)` : def.etiqueta}
            aria-label={def.etiqueta}
            maxLength={def.max_largo}
            error={mostrarErrores && falta}
            className={cx("h-10 text-sm", !esObligatorio && "border-line")}
          />
        );
      })}
    </div>
    </div>
  );
}

/**
 * ¿Falta algún dato obligatorio de este método?
 *
 * Se usa para deshabilitar el botón de cobrar ANTES de mandar la venta: es
 * mejor que el botón esté apagado a que el cajero lo pulse y reciba un error
 * del servidor con el cliente delante.
 */
export function faltanDatos(
  metodo: PaymentMethod | undefined,
  valores: Record<string, string>,
): boolean {
  if (!metodo) return false;
  return (metodo.campos_obligatorios ?? []).some((clave) => !(valores[clave] ?? "").trim());
}


/**
 * Rellena solo el teléfono y el documento con los del cliente de la venta.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ NO VOLVER A PEDIR LO QUE YA SE SABE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Si el cajero ya eligió a María —con su cédula y su teléfono en la ficha— y
 * después el método le pide «cédula del titular» y «teléfono», está tecleando
 * dos datos que Softfi tiene delante. Con cola detrás, eso es lo que hace que
 * se deje el campo vacío y después no haya con qué conciliar.
 *
 * ⚠️ Solo rellena lo VACÍO, y nunca pisa lo escrito. El pago puede venir de
 * otra persona —el marido, la hermana— y ahí el cajero corrige; si al elegir
 * cliente se le borrara lo que ya tecleó, sería peor que no ayudar.
 */
function useRellenarDelCliente({
  pedidos,
  valores,
  onCambiar,
  cliente,
}: {
  pedidos: string[];
  valores: Record<string, string>;
  onCambiar: (datos: Record<string, string>) => void;
  cliente?: { phone?: string | null; tax_id?: string | null } | null;
}) {
  useEffect(() => {
    if (!cliente) return;

    const nuevos: Record<string, string> = {};
    if (pedidos.includes("telefono") && !valores.telefono && cliente.phone) {
      nuevos.telefono = cliente.phone;
    }
    if (pedidos.includes("documento") && !valores.documento && cliente.tax_id) {
      nuevos.documento = cliente.tax_id;
    }

    if (Object.keys(nuevos).length > 0) onCambiar({ ...valores, ...nuevos });
    // `valores` no va en las dependencias a propósito: cambia con cada tecla, y
    // volver a entrar aquí en cada letra haría trabajo por nada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente?.phone, cliente?.tax_id, pedidos.join(",")]);
}
